import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Send, Loader2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('khadam_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ChatDrawer = ({ open, onClose, peerUserId, peerName, peerPhoto, peerSkill, bookingId }) => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMsgs = useCallback(async () => {
    if (!peerUserId) return;
    try {
      const { data } = await axios.get(`${API}/messages`, {
        headers: authHeaders(),
        params: { with_user: peerUserId },
      });
      setMessages(data);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
  }, [peerUserId, t]);

  useEffect(() => {
    if (!open || !peerUserId) return;
    setLoading(true);
    fetchMsgs().finally(() => setLoading(false));
    pollRef.current = setInterval(fetchMsgs, 5000);
    return () => clearInterval(pollRef.current);
  }, [open, peerUserId, fetchMsgs]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/messages`,
        { to_user_id: peerUserId, text, booking_id: bookingId || null },
        { headers: authHeaders() }
      );
      setMessages((m) => [...m, data]);
      setInput('');
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="chat-drawer-login-required">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center">
          <MessageCircle size={40} className="text-blue-500 mx-auto mb-3" />
          <p className="font-semibold text-gray-900 mb-2">{t('chat.loginRequired')}</p>
          <button
            onClick={onClose}
            className="mt-4 px-5 py-2 bg-blue-500 text-white rounded-xl font-semibold"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="chat-drawer"
    >
      <div
        className="bg-white w-full sm:w-[440px] h-[80vh] sm:h-[620px] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          {peerPhoto && <img src={peerPhoto} alt="" className="w-10 h-10 rounded-full object-cover" />}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 truncate" data-testid="chat-peer-name">{peerName}</h3>
            {peerSkill && <p className="text-xs text-gray-500 truncate">{peerSkill}</p>}
          </div>
          <button
            onClick={onClose}
            data-testid="chat-close-btn"
            aria-label={t('common.close')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50" data-testid="chat-messages">
          {loading && messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-blue-500" size={24} />
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">{t('chat.empty')}</div>
          )}
          {messages.map((m) => {
            const mine = m.from_user_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`message-${m.id}`}
                  className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    mine
                      ? 'bg-blue-500 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            );
          })}
          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-100 p-3 bg-white">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={t('chat.placeholder')}
              data-testid="chat-input"
              className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="chat-send-btn"
              aria-label={t('chat.send')}
              className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white rounded-full"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatDrawer;
